/**
 * Component for managing announcement email mailouts.
 *
 * Allows administrators to:
 * - Schedule an email to be sent to participants
 * - View the status of the scheduled email
 * - Cancel a scheduled email
 *
 * Only one mailout is allowed per announcement.
 *
 * @module components/programs/messages/edit-mailout
 */

import { IfAbility } from '@efdigital/ll-addon-auth';
import type { Message, MessageMailout } from '@efdigital/ll-addon-data';
import { DataTypeXceed } from '@efdigital/ll-addon-data/data-types/xceed';
import { setAsyncBelongsTo } from '@efdigital/ll-admin-v2/data/async-setter';
import { queryMailoutsForMessage } from '@efdigital/ll-admin-v2/data/builders/xceed/message-mailouts';
import { deepSave } from '@efdigital/ll-admin-v2/data/deep-save';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import { Request } from '@warp-drive/ember';
export interface EditMailoutSignature {
  Args: {
    /** The message to manage mailouts for. */
    message: Message;
  };
  Element: HTMLDivElement;
}

/**
 * Get the current active mailout (most recent pending/in-progress/complete).
 * Returns null if no active mailout exists.
 */
function getCurrentMailout(mailouts: MessageMailout[]): MessageMailout | null {
  // Prioritize: in-progress > pending > complete > disabled
  const inProgress = mailouts.find((m) => m.status === 'in-progress');
  if (inProgress) {
    return inProgress;
  }

  const pending = mailouts.find((m) => m.status === 'pending');
  if (pending) {
    return pending;
  }

  const complete = mailouts.find((m) => m.status === 'complete');
  if (complete) {
    return complete;
  }

  // Return the most recent disabled one if that's all we have
  const disabled = mailouts.find((m) => m.status === 'disabled');
  return disabled ?? null;
}

export class EditMailout extends Component<EditMailoutSignature> {
  @service declare store: Store;
  @tracked isSending = false;
  @tracked queryVersion = 0;

  get messageId(): string | null {
    return this.args.message.id;
  }

  get query() {
    // Include queryVersion to force re-query when incremented
    void this.queryVersion;
    if (!this.messageId) {
      return null;
    }
    return queryMailoutsForMessage(this.messageId);
  }

  reload = () => {
    this.queryVersion++;
  };

  /**
   * Schedule an email for this announcement.
   * Creates a new mailout with 'pending' status.
   */
  scheduleEmail = async () => {
    if (!this.messageId) {
      return;
    }

    this.isSending = true;
    try {
      const mailout = this.store.createRecord<MessageMailout>(
        DataTypeXceed.MessageMailout,
        {
          status: 'pending',
        }
      );
      setAsyncBelongsTo(mailout, 'message', this.args.message);
      await deepSave(mailout, this.store, { maxDepth: 1 });
      this.reload();
    } catch (error) {
      console.error('Failed to schedule email:', error);
    } finally {
      this.isSending = false;
    }
  };

  /**
   * Cancel a scheduled mailout.
   */
  cancelMailout = async (mailout: MessageMailout) => {
    try {
      mailout.status = 'disabled';
      await deepSave(mailout, this.store, { maxDepth: 1 });
      this.reload();
    } catch (error) {
      console.error('Failed to cancel mailout:', error);
    }
  };

  <template>
    <div class="edit-mailout" ...attributes>
      <IfAbility @ability="messages" @atLeast="editor">
        <:if>
          {{#if this.messageId}}
            <fieldset class="mb-4">
              <legend class="gud-eyebrow">
                <iconify-icon inline icon="ph:envelope-simple" />
                Email Notification
              </legend>

              <p class="text-muted small">
                Send this announcement as an email to all participants in the
                cohort. The email will be sent at the publication date/time.
              </p>

              {{#if this.query}}
                <Request @query={{this.query}}>
                  <:content as |mailouts|>
                    {{#let
                      (getCurrentMailout mailouts.data)
                      as |currentMailout|
                    }}
                      {{#if currentMailout}}
                        {{#if (eq currentMailout.status "complete")}}
                          <div class="alert alert-success mb-0">
                            <iconify-icon inline icon="ph:check-circle" />
                            Email has been sent to participants.
                          </div>
                        {{else if (eq currentMailout.status "in-progress")}}
                          <div class="alert alert-info mb-0">
                            <iconify-icon inline icon="ph:spinner" />
                            Email is currently being sent...
                          </div>
                        {{else if (eq currentMailout.status "pending")}}
                          <div class="alert alert-warning mb-3">
                            <iconify-icon inline icon="ph:clock" />
                            Email is scheduled to be sent.
                          </div>
                          <div class="text-center">
                            <button
                              type="button"
                              class="btn btn-sm btn-danger text-white"
                              {{on
                                "click"
                                (makeCancelHandler this currentMailout)
                              }}
                            >
                              <iconify-icon inline icon="ph:x" />
                              Cancel Email
                            </button>
                          </div>
                        {{else if (eq currentMailout.status "disabled")}}
                          <div class="alert alert-secondary mb-3">
                            <iconify-icon inline icon="ph:info" />
                            Email notification was cancelled.
                          </div>
                          {{! Allow scheduling a new one }}
                          <div class="text-center">
                            <button
                              type="button"
                              class="btn btn-sm btn-primary"
                              disabled={{this.isSending}}
                              {{on "click" this.scheduleEmail}}
                            >
                              <iconify-icon inline icon="ph:paper-plane-tilt" />
                              Schedule Email
                            </button>
                          </div>
                        {{/if}}
                      {{else}}
                        {{! No mailout exists - show schedule button }}
                        <div class="text-center">
                          <button
                            type="button"
                            class="btn btn-sm btn-primary"
                            disabled={{this.isSending}}
                            {{on "click" this.scheduleEmail}}
                          >
                            <iconify-icon inline icon="ph:paper-plane-tilt" />
                            Schedule Email
                          </button>
                        </div>
                      {{/if}}
                    {{/let}}
                  </:content>
                  <:error as |error|>
                    <div class="alert alert-danger small">
                      <iconify-icon inline icon="ph:warning" />
                      Failed to load email status:
                      {{error.message}}
                    </div>
                  </:error>
                  <:loading>
                    <div class="d-flex align-items-center text-muted small">
                      <span
                        class="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></span>
                      Loading email status...
                    </div>
                  </:loading>
                </Request>
              {{/if}}
            </fieldset>
          {{else}}
            <div class="alert alert-secondary small">
              <iconify-icon inline icon="ph:info" />
              Save the announcement first to enable email scheduling.
            </div>
          {{/if}}
        </:if>
      </IfAbility>
    </div>
  </template>
}

export default EditMailout;

function eq(a: string, b: string): boolean {
  return a === b;
}

function makeCancelHandler(
  component: EditMailout,
  mailout: MessageMailout
): () => void {
  return () => void component.cancelMailout(mailout);
}
